import React, {useRef} from 'react'
import {useRect} from '@reach/rect'
import classNames from 'classnames'
import {observer} from 'mobx-react-lite'
import {Box, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import {
  useBasketStore,
  useCustomerStore,
  useGlobalStore,
  useOrderStore,
} from '../../../store/hooks/useStore'
import {EditIcon} from '../../../components/icons'
import {ExitOrderEditModeModalContext} from '../use-exit-order-edit-mode-context'
import {ExitOrderEditModeModal} from '../../../components/edit-order-exit-mode-modal'
import {CartSummary} from './cart-summary'
import {AddPromocodeAction} from './add-promocode-action'
import {DeliveryAction} from './delivery-action'
import {ShoppingBasket} from './shopping-basket'
import {CartSummaryAccordion} from './cart-summary-accordion'
import {OrderEditModeAction} from './order-edit-mode-action'
import {actionBoxContainer, addPromocodeActionContainer, editModePageSubtitle} from './styles.css'
import useNavigation from '../../../hooks/use-navigation'
import {StatusMap} from '../../../types/commerce'
import {useBeforeYouGoPages} from '../../../hooks/use-before-you-go-pages'
import { useSlotExpiration } from '../../../store/hooks/useSlotExpiration'
import { useCarrierBag } from '../../../components/order-summary/use-carrier-bag'
import {useLocation} from 'react-router-dom'
import { ErrorMessageConfig } from '../../../utils/constants'
import { ErrorMessage } from '../../../components/error-message'

interface CartItemsProps {
  title: string
  checkoutButtonText?: string
}

interface LocationState {
  errorMessage?: ErrorMessageConfig
}

const CartItemsContainer = ({title, checkoutButtonText}: CartItemsProps) => {
  const location = useLocation()
  const locationState = location?.state as LocationState
  const {editMode, editingOrder} = useOrderStore()
  const isEditModeEnabled = editMode && editingOrder
  const shoppingBasketRef = useRef<HTMLDivElement>(null)
  const rect = useRect(shoppingBasketRef, {observe: false})
  const exitEditModeModal = useDisclosure()
  const navigate = useNavigation()
  const {setShowCheckoutSummary} = useGlobalStore()
  const {status, currentDeliverySlotId} = useBasketStore()
  const {isReservationExpired} = useSlotExpiration()
  const {isRegistered} = useCustomerStore()
  const {currentPage: beforeYouGoPage} = useBeforeYouGoPages()
  const {includeBag, setIncludeBag} = useCarrierBag()

  const handleCheckoutButtonClick = () => {

    if (!isRegistered) {
      navigate('/login', 'push', {
        redirectTo: '/basket'
      })
      return
    }

    if (checkoutButtonText === 'Continue to Payment') {
      setShowCheckoutSummary(false)
      navigate('/checkout/summary', 'replace')
      return
    }

    if (!beforeYouGoPage) {
      navigate('/checkout/summary')
      return
    }

    if ((status !== StatusMap.INIT && !currentDeliverySlotId) || isReservationExpired()) {
      navigate('/book-delivery', 'push', {
        redirectTo: beforeYouGoPage ? '/Iceland-BeforeYouGoPages' : '/checkout/summary',
      })

      return
    }

    beforeYouGoPage && navigate('/Iceland-BeforeYouGoPages')
  }

  return (
    <ExitOrderEditModeModalContext.Provider value={exitEditModeModal}>
      <Stack spacing={['12px', '44px']}>
        {isEditModeEnabled ? (
          <Flex align="flex-start" gap="4px" paddingX={['16px', '0px']}>
            <EditIcon boxSize={['16px', '28px']} color="promotionColorDark" />
            <Stack width="full" spacing="4px">
              <Text as="h1" color="promotionColorDark" variant="heading5" data-test-selector="cart-editing-order-header">
                Editing Your Order
              </Text>
              <Text as="span" className={editModePageSubtitle} lineHeight="shorter" data-test-selector="basket-review-order-number">
                Basket Review - Order #{editingOrder?.orderNo}
              </Text>
            </Stack>
          </Flex>
        ) : (
          <Text as="h1" variant="heading5" paddingX={['16px', '0px']}>
            {title}
          </Text>
        )}
        <Stack spacing={['0px', '16px']}>
        {locationState?.errorMessage  && <ErrorMessage errorMessage={locationState.errorMessage} />}
          <Flex direction={['column', 'row']} flexWrap="wrap" justify="space-between">
            <Box className={actionBoxContainer} bg="white" alignSelf="stretch">
              {isEditModeEnabled ? (
                <OrderEditModeAction shoppingBasketRect={rect} />
              ) : (
                <DeliveryAction />
              )}
            </Box>
            {/** Mobile Summary **/}
            <CartSummaryAccordion
              checkoutButtonText={checkoutButtonText ?? ''}
              handleCheckoutButtonClick={handleCheckoutButtonClick}
              display={['flex', 'none']}
              includeBag={includeBag}
              setIncludeBag={setIncludeBag}
            />
            {/** Desktop Summary **/}
            <CartSummary
              checkoutButtonText={checkoutButtonText ?? ''}
              handleCheckoutButtonClick={handleCheckoutButtonClick}
              includeBag={includeBag}
              setIncludeBag={setIncludeBag}
            />
            <AddPromocodeAction />
          </Flex>
          <ShoppingBasket
            checkoutButtonText={checkoutButtonText ?? ''}
            handleCheckoutButtonClick={handleCheckoutButtonClick}
            ref={shoppingBasketRef}
            includeBag={includeBag}
            setIncludeBag={setIncludeBag}
          />
        </Stack>
      </Stack>
      <ExitOrderEditModeModal {...exitEditModeModal} />
    </ExitOrderEditModeModalContext.Provider>
  )
}

export const CartItemsSkeleton = () => {
  return (
    <Stack spacing={['12px', '44px']}>
      <Text as="h1" variant="heading5" paddingX={['16px', '0px']}>
        Your shopping basket
      </Text>
      <Stack spacing={['0px', '16px']}>
        <Flex direction={['column', 'row']} flexWrap="wrap" justify="space-between">
          <Box
            className={actionBoxContainer}
            bg="gray100"
            alignSelf="stretch"
            style={{minHeight: 154}}
          />
          <Box
            className={actionBoxContainer}
            bg="gray100"
            style={{height: 475}}
            display={['none', 'block']}
          />
          <Box
            bg="gray100"
            height="64px"
            marginTop="12px"
            className={classNames(actionBoxContainer, addPromocodeActionContainer)}
          />
        </Flex>
        <Box bg="gray100" style={{height: 300}} />
      </Stack>
    </Stack>
  )
}

export default observer(CartItemsContainer)
